import { Injectable } from '@angular/core';
import { Message, MessageService } from 'primeng/api';

export const CLINIC_TOAST_KEY = 'clinic';

export type ClinicToastSeverity = 'success' | 'info' | 'warn' | 'error';

const DEFAULT_SUMMARY: Record<ClinicToastSeverity, string> = {
  success: 'Success',
  info: 'Notice',
  warn: 'Heads up',
  error: 'Error',
};

const DEFAULT_LIFE: Record<ClinicToastSeverity, number> = {
  success: 3500,
  info: 4500,
  warn: 5500,
  error: 7000,
};

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  constructor(private messageService: MessageService) {}

  success(summary: string, detail: string, life = DEFAULT_LIFE.success): void {
    this.emit('success', summary, detail, life);
  }

  error(summary: string, detail: string, life = DEFAULT_LIFE.error): void {
    this.emit('error', summary, detail, life);
  }

  info(summary: string, detail: string, life = DEFAULT_LIFE.info): void {
    this.emit('info', summary, detail, life);
  }

  warn(summary: string, detail: string, life = DEFAULT_LIFE.warn): void {
    this.emit('warn', summary, detail, life);
  }

  /** Pass-through with clinic defaults (key, life, summary, closable). */
  add(message: Message): void {
    const severity = (message.severity ?? 'info') as ClinicToastSeverity;

    this.messageService.add({
      ...message,
      key: message.key ?? CLINIC_TOAST_KEY,
      closable: message.closable ?? true,
      severity,
      summary: message.summary ?? DEFAULT_SUMMARY[severity] ?? 'Notice',
      life: message.life ?? DEFAULT_LIFE[severity] ?? DEFAULT_LIFE.info,
    });
  }

  clear(key = CLINIC_TOAST_KEY): void {
    this.messageService.clear(key);
  }

  private emit(
    severity: ClinicToastSeverity,
    summary: string,
    detail: string,
    life: number
  ): void {
    this.add({ severity, summary, detail, life });
  }
}
